import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Filters from "@/components/Filters";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CAT_NAMES = { vetements: "Vêtements", bijoux: "Bijoux", decoration: "Décoration & Objets", cosmetiques: "Cosmétiques & Beauté" };
const CAT_INTRO = {
  vetements: "Des silhouettes signées par des créateurs indépendants, en matières nobles.",
  bijoux: "Joaillerie contemporaine et pièces façonnées main.",
  decoration: "Objets sculpturaux et céramiques d'atelier pour un intérieur singulier.",
  cosmetiques: "Beauté d'exception : formulations rares et packshots éditoriaux.",
};
const SORTS = [
  { v: "new", l: "Nouveautés" }, { v: "selection", l: "Sélection" }, { v: "price_asc", l: "Prix croissant" },
  { v: "price_desc", l: "Prix décroissant" }, { v: "popular", l: "Popularité" }, { v: "rating", l: "Mieux notés" },
];

export default function Catalog() {
  const { slug } = useParams();
  const [sp, setSp] = useSearchParams();
  const [data, setData] = useState({ products: [], total: 0 });
  const [facets, setFacets] = useState({});
  const [creators, setCreators] = useState([]);
  const [bounds, setBounds] = useState([0, 1000]);
  const [loading, setLoading] = useState(true);
  const [mobileFilters, setMobileFilters] = useState(false);

  const selected = Object.fromEntries(sp.entries());
  const sort = selected.sort || "new";

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const params = { ...selected };
    if (slug) params.category = slug;
    const [prodRes, facetRes] = await Promise.all([
      api.get("/products", { params }),
      api.get("/products/facets", { params: slug ? { category: slug } : {} }),
    ]);
    setData(prodRes.data);
    setFacets(facetRes.data.facets);
    setCreators(facetRes.data.creators);
    setBounds([Math.floor(facetRes.data.price_min), Math.ceil(facetRes.data.price_max)]);
    setLoading(false);
  }, [slug, sp]); // eslint-disable-line

  useEffect(() => { fetchAll(); window.scrollTo(0, 0); }, [fetchAll]);

  const setParam = (k, v) => {
    const n = new URLSearchParams(sp);
    if (v === null || v === "" || v === undefined) n.delete(k); else n.set(k, v);
    setSp(n);
  };
  const toggle = (key, val) => {
    const cur = (selected[key] || "").split(",").filter(Boolean);
    const next = cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val];
    setParam(key, next.join(","));
  };
  const reset = () => { const n = new URLSearchParams(); if (selected.q) n.set("q", selected.q); setSp(n); };

  const title = slug ? CAT_NAMES[slug] : selected.q ? `« ${selected.q} »` : "Toutes les créations";
  const filtersEl = (
    <Filters facets={facets} creators={creators} selected={selected} priceBounds={bounds}
      onToggle={toggle} onPrice={(k, v) => setParam(k, v)} onCreator={(id) => setParam("creator", selected.creator === id ? null : id)} onReset={reset} />
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-10 lg:py-16">
      <div className="mb-10">
        <span className="over text-[0.62rem] text-[#D4AF37]">Catalogue</span>
        <h1 className="font-display text-4xl lg:text-5xl font-light mt-2">{title}</h1>
        {slug && <p className="text-neutral-500 mt-3 max-w-xl">{CAT_INTRO[slug]}</p>}
      </div>

      <div className="flex gap-10">
        <aside className="hidden lg:block w-64 shrink-0">{filtersEl}</aside>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-neutral-100">
            <div className="flex items-center gap-3">
              <button onClick={() => setMobileFilters(true)} className="lg:hidden over text-[0.62rem] inline-flex items-center gap-2 border border-neutral-200 px-3 py-2" data-testid="mobile-filters-button">
                <SlidersHorizontal size={14} /> Filtres
              </button>
              <span className="text-sm text-neutral-500" data-testid="results-count">{data.total} résultat{data.total > 1 ? "s" : ""}</span>
            </div>
            <Select value={sort} onValueChange={(v) => setParam("sort", v)}>
              <SelectTrigger className="w-44 rounded-none border-neutral-200 over text-[0.62rem]" data-testid="sort-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SORTS.map((s) => <SelectItem key={s.v} value={s.v} className="text-sm">{s.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-[3/4] bg-neutral-100 animate-pulse" />)}
            </div>
          ) : data.products.length === 0 ? (
            <div className="py-24 text-center">
              <p className="font-display text-2xl">Aucune création trouvée</p>
              <button onClick={reset} className="btn btn-outline mt-6">Réinitialiser les filtres</button>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-8 lg:gap-x-8">
              {data.products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          )}
        </div>
      </div>

      <Sheet open={mobileFilters} onOpenChange={setMobileFilters}>
        <SheetContent side="left" className="w-80 overflow-y-auto p-6"><SheetTitle className="sr-only">Filtres</SheetTitle>{filtersEl}</SheetContent>
      </Sheet>
    </div>
  );
}
